import Component from '../../../assets/scripts/modules/component'
import insertScript from '../../../assets/scripts/utilities/insert-script'
import randomHash from '../../../assets/scripts/utilities/random-hash'
import CookieNoticeComponent from '../cookie-notice/cookie-notice'

// Ensure we have access to component loading if needed, though we import class directly to use logic
// However since the HTML includes it, it will be initialized separately as its own component instance
// But we need to interact with it.

export default class VideoEmbedComponent extends Component {
  init () {
    this.container = this.element.querySelector('.video-embed__container')
    this.hitTarget = this.element.querySelector('.video-embed__hit-target')
    this.autoplay = this.element.classList.contains('video-embed--autoplay')
    this.picture = this.element.querySelector('picture')
    this.embedTitle = this.element.querySelector('.video-embed__title')
    this.cookieNotice = this.element.querySelector('.cookie-notice')
    this.cookieConsentEnabled = this.element.getAttribute('data-cookie-consent-enabled') !== 'False'
    this.euVideoFitCover = this.element.getAttribute('data-eu-video-fit-cover') === 'true'
    // Track whether the user explicitly clicked play on this video instance
    this.pendingPlay = false

    if (!this.container || !this.hitTarget) {
      return
    }

    this.videoId = this.hitTarget.getAttribute('data-id')
    this.videoType = this.hitTarget.getAttribute('data-type')

    if (!this.videoId || !this.videoType) {
      return
    }

    // Initialize cookie notice behavior
    if (this.cookieNotice) {
      // Manually initialize the component logic for the specific instance found inside
      new CookieNoticeComponent(this.cookieNotice).init()

      // Handle the accept event specifically for this video to play immediately
      this.cookieNotice.querySelector('.cookie-notice__button--accept')?.addEventListener('click', (e) => {
        e.preventDefault()
        if (window.Cookiebot) {
          // Determine user current consent state and add marketing
          // Note: submitCustomConsent triggers a reload in some configurations
          // We rely on the reload or callback. If no reload, we proceed.
          window.Cookiebot.submitCustomConsent(
            window.Cookiebot.consent.preferences,
            window.Cookiebot.consent.statistics,
            true // marketing
          )
          // forceCheckAndPlay will be triggered by CookiebotOnAccept
        } else {
          // No Cookiebot available, just hide the notice
          this.cookieNotice.hidden = true
        }
      })

      // Decline: hide the notice and reset pendingPlay, stay on placeholder
      this.cookieNotice.querySelector('.cookie-notice__button--decline')?.addEventListener('click', (e) => {
        e.preventDefault()
        this.pendingPlay = false
        this.cookieNotice.hidden = true
      })
    }

    this.iframeId = `player-${randomHash()}`
    this.player = null

    // CookiebotOnAccept only plays this video if the user explicitly clicked play
    window.addEventListener('CookiebotOnAccept', () => {
      if (this.pendingPlay) {
        this.pendingPlay = false
        this.forceCheckAndPlay()
      } else {
        // Consent is now available — just hide the notice and show the play button
        if (this.cookieNotice) {
          this.cookieNotice.hidden = true
        }
      }
    })

    window.addEventListener('CookiebotOnDecline', () => {
      this.pendingPlay = false
      if (this.player) {
        this.player.stopVideo()
      }
      // Only show the notice if the user was actively trying to play
      // Don't show on page load when cookies were previously declined
    })

    window.addEventListener('stop-player', () => {
      if (this.player) {
        this.player.stopVideo()
      }
    })

    this.hitTarget.addEventListener('click', this.onHitTargetClick.bind(this))

    if (this.autoplay) {
      window.setTimeout(() => this.hitTarget.click(), 150) // Arbitrary timeout to make sure the wrapper styling is painted first.
    }
  }

  forceCheckAndPlay () {
    // Hide notice if visible
    if (this.cookieNotice) {
      this.cookieNotice.hidden = true
    }

    // If we have consent now, proceed to load/play
    if (window.Cookiebot && window.Cookiebot.consent.marketing) {
      if (this.videoType === 'vimeo') {
        this.player ? this.player.playVideo() : this.insertVimeoContainer()
      } else if (this.videoType === 'youtube' || this.videoType === 'youtube-playlist') {
        if (window.YT) {
          this.player ? this.player.playVideo() : (this.videoType === 'youtube' ? this.insertYouTubeContainer() : this.insertYouTubePlaylistContainer())
        } else {
          // Insert script will trigger callback
          this.hitTarget.click()
        }
      }
    }
  }

  onHitTargetClick (event) {
    if (this.videoType === 'vimeo') {
      if (window.Cookiebot && window.Cookiebot.consent.marketing) {
        event.preventDefault()
        if (window.video_vimeoLoaded) {
          this.player ? this.player.playVideo() : this.insertVimeoContainer()
        } else {
          insertScript(
            'vimeo-api-script',
            'https://player.vimeo.com/api/player.js',
            this.insertVimeoContainer.bind(this)
          )
        }
      } else {
        if (this.cookieConsentEnabled) {
          event.preventDefault()
          this.pendingPlay = true
          this.showConsentNotice()
        }
      }
    } else if (this.videoType === 'youtube') {
      if (window.Cookiebot && window.Cookiebot.consent.marketing) {
        event.preventDefault()
        if (window.YT) {
          this.player ? this.player.playVideo() : this.insertYouTubeContainer()
        } else {
          insertScript(
            'youtube-api-script',
            '//youtube.com/iframe_api',
            this.insertYouTubeContainer.bind(this)
          )
        }
      } else {
        if (this.cookieConsentEnabled) {
          event.preventDefault()
          this.pendingPlay = true
          this.showConsentNotice()
        }
      }
    } else if (this.videoType === 'youtube-playlist') {
      if (window.Cookiebot && window.Cookiebot.consent.marketing) {
        event.preventDefault()
        if (window.YT) {
          this.player ? this.player.playVideo() : this.insertYouTubePlaylistContainer()
        } else {
          insertScript(
            'youtube-api-script',
            '//youtube.com/iframe_api',
            this.insertYouTubePlaylistContainer.bind(this)
          )
        }
      } else {
        if (this.cookieConsentEnabled) {
          event.preventDefault()
          this.pendingPlay = true
          this.showConsentNotice()
        }
      }
    } else if (this.videoType === 'euvideo') {
      event.preventDefault()
      this.insertEUVideoContainer()
    } else {
      throw Error(`Video #${this.videoId}: Video type "${this.videoType}" not supported`)
    }
  }

  showConsentNotice () {
    if (this.cookieNotice) {
      this.cookieNotice.hidden = false
    } else {
      // Fallback if no component found (legacy)
      if (window.Cookiebot) {
        window.Cookiebot.show()
      }
    }
  }

  /* jshint ignore:start */
  async insertVimeoContainer () {
    if (!window.Vimeo) {
      throw Error('Vimeo API not loaded correctly.')
    }

    this.vimeoOptions = {
      id: this.videoId, // can be either the ID or URL to the video
      autopause: true,
      autoplay: true,
      background: false, // Hide controls and autoplay
      byline: false, // Show byline
      color: 'ffffff',
      // height: 0,
      loop: false,
      // maxheight: 0,
      // maxwidth: 0,
      muted: false,
      playsinline: false, // Set to true to play inline on some mobile devices
      portrait: false, // Show the video portrait
      speed: false, // Show speed controls
      title: false, // Show title
      transparent: true
      // width: 0,
    }

    this.player = await new window.Vimeo.Player(this.container, this.vimeoOptions)
    this.container.removeChild(this.hitTarget)
  }
  /* jshint ignore:end */

  insertYouTubeContainer () {
    const div = document.createElement('div')

    div.id = this.iframeId
    div.className = 'video-embed__iframe'

    this.hitTarget.parentNode.replaceChild(div, this.hitTarget)
    this.hitTarget = null

    const afterLoad = () => {
      this.player = new window.YT.Player(this.iframeId, {
        width: 640,
        height: 360,
        videoId: this.videoId,
        playerVars: { autoplay: 1, hd: 1, showinfo: 0, modestbranding: 1, iv_load_policy: 3, rel: 0, origin: `${window.location.protocol}//${window.location.host}` },
        events: {
          onReady: () => {
            this.player.setPlaybackQuality('hd1080')

            if (this.element.getAttribute('data-autoplay') !== null) {
              this.player.playVideo()
            }
          }
        }
      })

      window.youtubeLoaded = true
    }

    if (!window.youtubeLoaded && (!window.YT || !window.YT.loaded)) {
      window.onYouTubeIframeAPIReady = afterLoad
    } else {
      afterLoad()
    }
  }

  insertYouTubePlaylistContainer () {
    const div = document.createElement('div')

    div.id = this.iframeId
    div.className = 'video-embed__iframe'

    this.hitTarget.parentNode.replaceChild(div, this.hitTarget)
    this.hitTarget = null

    const afterLoad = () => {
      this.player = new window.YT.Player(this.iframeId, {
        width: 640,
        height: 360,
        playerVars: { listType: 'playlist', list: this.videoId, autoplay: 1, hd: 1, showinfo: 0, modestbranding: 1, iv_load_policy: 3, rel: 0, origin: `${window.location.protocol}//${window.location.host}` },
        events: {
          onReady: () => {
            this.player.setPlaybackQuality('hd1080')

            if (this.element.getAttribute('data-autoplay') !== null) {
              this.player.playVideo()
            }
          }
        }
      })

      window.youtubeLoaded = true
    }

    if (!window.youtubeLoaded) {
      window.onYouTubeIframeAPIReady = afterLoad
    } else {
      afterLoad()
    }
  }

  insertEUVideoContainer () {
    // Create an iframe for EU Video CDN
    const iframe = document.createElement('iframe')

    iframe.id = this.iframeId
    iframe.className = 'video-embed__iframe'
    iframe.title = this.embedTitle ? this.embedTitle.textContent : 'EU Video'
    let src = `https://cnv.euvideocdn.com/player/index.html?id=${this.videoId}&autoplay=1`
    if (this.euVideoFitCover) {
      src += '&fit=cover'
    }
    iframe.src = src
    iframe.frameBorder = '0'
    iframe.allowFullscreen = true
    iframe.allow = 'autoplay; fullscreen; picture-in-picture; clipboard-write'
    iframe.style.border = 'none'
    iframe.style.width = '100%'
    iframe.style.height = '100%'

    this.hitTarget.parentNode.replaceChild(iframe, this.hitTarget)
    this.hitTarget = null
    this.player = iframe // Store reference to iframe
  }
}

window.addEventListener('DOMContentLoaded', () => {
  for (const element of document.querySelectorAll('.video-embed')) {
    element.instance = element.instance || new VideoEmbedComponent(element)
  }
})
